// utilities/phoneNumber.js
// ─────────────────────────────────────────────────────────────────────────────
// One place that turns a stored phone number into something you can dial,
// message or hand to wa.me.
//
// THE TRUNK ZERO. Numbers are stored the way people write them here —
// "0700771301" — and every channel that is not a local phone call needs
// "256700771301" instead. Dropping the leading 0 and prefixing the market's
// dial code is four lines, which is why this dashboard already contains at
// least three separate copies of it (ProcessRefundForm, WalletDashboard, and
// the server mirror quoted in VendorAlertSettings) and several wa.me links
// built with a bare `String(phone).replace(/\D/g, "")`, which is NOT the same
// thing: that produces "0700771301", and wa.me answers such a link with
// "phone number shared via url is invalid". A share button that silently opens
// an empty WhatsApp is worse than no share button.
//
// The dial code comes from config/markets.js, so a number is normalised for
// the market it belongs to rather than assuming Uganda.
// ─────────────────────────────────────────────────────────────────────────────

import { getMarket, normalizeCountry } from "../config/markets.js";
import { getActiveCountry } from "./activeCountry.js";

/**
 * International format, digits only, no plus — what wa.me expects.
 * Returns null when there is nothing usable, so a caller can hide the control
 * rather than open a broken link.
 *
 * @param {string} phone
 * @param {string} [country] ISO-2; defaults to the active market
 */
export const toMsisdn = (phone, country) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;

  const market = getMarket(normalizeCountry(country) || getActiveCountry());
  const dial = String(market?.dialCode || "").replace(/\D/g, "");

  // Already international, for this market or any other.
  if (dial && digits.startsWith(dial) && digits.length > dial.length) {
    return digits;
  }
  // Local, with the trunk zero: 0700771301 -> 256700771301
  if (digits.startsWith("0")) {
    return dial ? dial + digits.slice(1) : digits.slice(1);
  }
  // Local, without it: 700771301 -> 256700771301
  if (dial && digits.length <= 10) return dial + digits;

  return digits;
};

/** Human spacing for display. Never use this for a link. */
export const formatPhoneForDisplay = (phone) => {
  const s = String(phone || "").trim();
  return s || null;
};

/**
 * A wa.me link, or null when the number cannot be made international.
 * Passing no phone gives WhatsApp's "choose a chat" share sheet instead, which
 * is the right fallback when nobody is assigned yet.
 */
export const whatsAppLink = (phone, text, country) => {
  const body = text ? `?text=${encodeURIComponent(text)}` : "";
  const msisdn = phone ? toMsisdn(phone, country) : null;
  if (phone && !msisdn) return null;
  return msisdn ? `https://wa.me/${msisdn}${body}` : `https://wa.me/${body}`;
};

export default toMsisdn;
